import { useEffect, useState } from 'react';
import type { FC } from 'react';

import Head from 'next/head';

import classnames from 'classnames';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import LinkButton from 'src/app/components/Elements/Button/LinkButton';
import { DefaultPhoneNumber } from 'src/app/components/Elements/Location';
import { getStarted, loggedInQuestions, loggedInTransferRx, questions } from 'src/app/router/paths';
import { segmentTrack } from 'src/app/state/actions';
import { getCurrentPathname, isLoggedIn } from 'src/app/state/selectors';
import type { Dispatch, RootState } from 'src/app/state/store';
import { CAPSULE_HELP_EMAIL } from 'src/consts';

import { BrokenHeart } from './BrokenHeart/BrokenHeart';
import styles from './styles.module.scss';

interface RectangleProps {
  isLoggedIn?: boolean;
  beginOnboardingAnalytics?: () => void;
  handleTransferRxButtonClicked?: () => void;
}

const RECTANGLES = [
  ({ isLoggedIn }: RectangleProps) => (
    <div className={styles.rectangle}>
      <div className={styles.icon}>
        <img src="/public/receipt.svg" />
      </div>
      <h4>Questions</h4>
      <p>Questions about your insurance, copay, or anything more general?</p>
      <LinkButton
        to={isLoggedIn ? loggedInQuestions.toPath() : questions.toPath()}
        style="checkout"
        size="medium"
        block
        data-cy="learnMore"
      >
        Learn more
      </LinkButton>
    </div>
  ),
  ({ isLoggedIn, beginOnboardingAnalytics, handleTransferRxButtonClicked }: RectangleProps) => {
    const getRedirectPath = isLoggedIn => {
      if (isLoggedIn) return loggedInTransferRx.toPath();
      return getStarted.toPath();
    };
    return (
      <div className={styles.rectangle}>
        <div className={styles.icon}>
          <img src="/public/pill-bottle.svg" />
        </div>
        <h4>Transfer Rx</h4>
        <p>Transfer existing or additional prescriptions to Capsule</p>
        <LinkButton
          to={getRedirectPath(isLoggedIn)}
          style="checkout"
          size="medium"
          block
          data-cy="startNow"
          onClick={!isLoggedIn ? beginOnboardingAnalytics : handleTransferRxButtonClicked}
        >
          Start now
        </LinkButton>
      </div>
    );
  },
  () => (
    <div className={styles.rectangle}>
      <div className={styles.icon}>
        <img src="/public/chat.svg" />
      </div>
      <h4>Contact us</h4>
      <p>
        Call or Text: <DefaultPhoneNumber className={classnames(styles.ignore, 'green')} />
        <br />
        Email:{' '}
        <a className={classnames(styles.ignore, 'green')} href={`mailto:${CAPSULE_HELP_EMAIL}`}>
          {CAPSULE_HELP_EMAIL}
        </a>
      </p>
    </div>
  ),
];

const mapStateToProps = (state: RootState) => {
  return {
    isLoggedIn: isLoggedIn(state),
    pathname: getCurrentPathname(state),
  };
};
const mapDispatchToProps = (dispatch: Dispatch) => ({
  dispatchSegmentTrack: (event: UnifiedEvent, payload) => dispatch(segmentTrack(event, payload)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const NotFoundRenderer: FC<PropsFromRedux> = ({ isLoggedIn, pathname, dispatchSegmentTrack }) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const beginOnboardingAnalytics = () => {
    dispatchSegmentTrack(UNIFIED.beginOnboarding, {
      pagepath: pathname,
      source: 'Not Found Page',
    });
  };

  const handleTransferRxButtonClicked = () => {
    dispatchSegmentTrack(UNIFIED.viewTransferForm, { location: 'Not Found' });
  };

  return (
    <>
      <Head>
        <meta name="robots" content="noindex" />
      </Head>
      <div className={styles.component}>
        <div className={styles.header}>
          <div className={styles.text}>
            <h1>Sorry dear</h1>
            <p>We weren&#8217;t able to find that page for you.</p>
          </div>
          <div className={styles.brokenHeart}>
            <BrokenHeart mounted={mounted} />
          </div>
        </div>

        <div className={styles.helpfulLinks}>
          <h3>Helpful Links</h3>
          <div className={styles.rectangles}>
            {RECTANGLES.map((Rectangle, i) => {
              return (
                <Rectangle
                  isLoggedIn={isLoggedIn}
                  beginOnboardingAnalytics={beginOnboardingAnalytics}
                  handleTransferRxButtonClicked={handleTransferRxButtonClicked}
                  key={i}
                />
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

NotFoundRenderer.displayName = 'NotFound';
const NotFound = connector(NotFoundRenderer);
export default NotFound;
